import { CloseCircleIcon, PlusCircleIcon } from '~/svgs/home-page';
import { NextLink, Translate, useTranslate } from '~/i18n';
import React, { useState } from 'react';

import Button from '../common/button';
import Container from '../common/container';
import ImageProxy from '../common/image/image-proxy';
import cn from 'classnames';

interface Destination {
   slug: string;
   title: string;
   description: string;
   imageUrl: string;
}

const DestinationCard: React.FC<Destination> = ({ title, description, imageUrl, slug }) => {
   const [isExpanded, setIsExpanded] = useState(false);
   const toggleExpand = () => {
      setIsExpanded(!isExpanded);
   };
   return (
      <div
         className={
            'relative flex flex-col w-full h-80 md:h-96 transition-all rounded-2xl lg:h-[600px] aspect-[16/9] justify-end'
         }>
         <NextLink href={`/destinations/${slug}`}>
            <ImageProxy
               src={imageUrl?.split(',')?.[0] || '/images/not_found.svg'}
               className="object-cover rounded-2xl"
               alt={title}
               layout="fill"
            />
         </NextLink>
         <div
            className={cn(
               'relative bottom-0 inline-flex flex-col px-4 py-6 transition-all h-fit bg-black/80 ltr:left-0 rtl:right-0',
               { 'w-full h-full': isExpanded }
            )}>
            <div className="flex items-center justify-between w-full gap-4">
               <div className="text-xl leading-none text-white line-clamp-1">{title}</div>

               {!isExpanded && (
                  <PlusCircleIcon
                     className="flex w-8 h-8 text-white transition-all cursor-pointer shrink-0 hover:scale-110 hover:transition-all"
                     onClick={toggleExpand}
                  />
               )}
            </div>

            {isExpanded && (
               <div className="mt-8 space-y-8">
                  <div className="text-sm font-normal text-white line-clamp-4 lg:line-clamp-none lg:text-lg">
                     {description}
                  </div>

                  <Button type="outline-white" className="w-fit">
                     <NextLink href={`/destinations/${slug}`}>
                        <Translate id="common:explore" />
                     </NextLink>
                  </Button>

                  <CloseCircleIcon
                     className="absolute w-8 h-8 text-white transition-all cursor-pointer bottom-8 right-4 hover:scale-110 hover:transition-all"
                     onClick={toggleExpand}
                  />
               </div>
            )}
         </div>
      </div>
   );
};

interface DestinationData {
   id: string;
   Name: string;
   Description: string;
   Icon: string;
   ContentIdFK: string;
   title: { [key: string]: string };
   short_description: { [key: string]: string };
   images: string[];
   slug: { [key: string]: string };
}

interface DestinationsProps {
   data: DestinationData[];
}

const Destinations = ({ data }: DestinationsProps) => {
   const { locale } = useTranslate();
   return (
      <section className="py-8 bg-black lg:py-32">
         <Container>
            <div className="flex flex-col justify-between w-full h-full gap-4 lg:items-center lg:flex-row">
               <div>
                  <div className="text-lg font-normal text-[#727272] uppercase">
                     <Translate id="common:explore" />{' '}
                  </div>
                  <div className="text-4xl font-normal text-white">
                     <Translate id="common:destinations" />
                  </div>
               </div>
               <Button type="outline-white">
                  <NextLink href="/madinah/destinations">
                     <Translate id="common:all destinations" />
                  </NextLink>
               </Button>
            </div>

            <div className="grid w-full h-full grid-cols-1 gap-8 mt-8 lg:grid-cols-3 lg:mt-12">
               {data
                  ?.slice(0, 3)
                  ?.map(destination => (
                     <DestinationCard
                        key={destination?.id}
                        title={destination?.title?.[locale]}
                        description={destination?.short_description?.[locale]}
                        imageUrl={destination?.images?.[0]}
                        slug={destination?.slug?.[locale]}
                     />
                  ))}
            </div>
         </Container>
      </section>
   );
};

export default Destinations;
